import {
  ITENS_DO_FUNIL_NO_CRM,
  TIPOS_DE_CAMPO_MONETARIOS,
  type ConexaoDoKommo,
  type EtapaSelecionada,
  type ItemDoFunilNoCrm,
  type MapeamentoDoFunil,
} from "@base/contracts";
import { Button, CampoDeSelecao, cn } from "@base/ui";
import { CheckCircle2, LoaderCircle, Megaphone } from "lucide-react";
import { useState } from "react";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { DESCRICAO_DA_ETAPA, ROTULO_DA_ETAPA } from "../../dashboard";
import { useMapearKommo } from "../application/conexoes.contexto";
import { SeletorDeEtapa } from "./seletor-de-etapa";

/** Etapa fixa de ganho de todo pipeline do Kommo. */
const ETAPA_GANHA = 142;

const UTMS = [
  { chave: "utmCampanhaCampoId", rotulo: "Campanha", descricao: "Qual campo do lead vira a coluna Campanha.", padrao: "utm_campaign" },
  { chave: "utmConjuntoCampoId", rotulo: "Conjunto", descricao: "Qual campo do lead vira a coluna Conjunto.", padrao: "utm_content" },
  { chave: "utmAnuncioCampoId", rotulo: "Anúncio", descricao: "Qual campo do lead vira a coluna Anúncio.", padrao: "utm_term" },
] as const;

type Rascunho = Partial<Record<ItemDoFunilNoCrm, EtapaSelecionada>> & {
  readonly tratamento?: EtapaSelecionada;
  readonly valorConsultaCampoId?: string;
  readonly valorPropostoCampoId?: string;
  readonly utmCampanhaCampoId?: string;
  readonly utmConjuntoCampoId?: string;
  readonly utmAnuncioCampoId?: string;
};

/** Sem mapeamento salvo, a Venda ja vem na etapa de ganho do funil principal. */
function rascunhoInicial(conexao: ConexaoDoKommo): Rascunho {
  const mapeamento = conexao.mapeamento;
  if (mapeamento) {
    return {
      crm: mapeamento.crm,
      mql: mapeamento.mql,
      agendado: mapeamento.agendado,
      consulta: mapeamento.consulta,
      venda: mapeamento.venda,
      ...(mapeamento.tratamento ? { tratamento: mapeamento.tratamento } : {}),
      valorConsultaCampoId: mapeamento.valorConsultaCampoId ? String(mapeamento.valorConsultaCampoId) : "",
      valorPropostoCampoId: mapeamento.valorPropostoCampoId ? String(mapeamento.valorPropostoCampoId) : "",
      ...idsDasUtms(conexao, mapeamento),
    };
  }
  const principal = conexao.pipelines.find((pipeline) => pipeline.principal) ?? conexao.pipelines[0];
  return {
    ...(principal ? { venda: { pipelineId: principal.id, etapaId: ETAPA_GANHA } } : {}),
    ...idsDasUtms(conexao, null),
  };
}

function completo(rascunho: Rascunho): MapeamentoDoFunil | null {
  const { crm, mql, agendado, consulta, venda } = rascunho;
  if (!crm || !mql || !agendado || !consulta || !venda) return null;
  return {
    crm,
    mql,
    agendado,
    consulta,
    venda,
    ...(rascunho.tratamento ? { tratamento: rascunho.tratamento } : {}),
    valorConsultaCampoId: rascunho.valorConsultaCampoId ? Number(rascunho.valorConsultaCampoId) : null,
    valorPropostoCampoId: rascunho.valorPropostoCampoId ? Number(rascunho.valorPropostoCampoId) : null,
    utmCampanhaCampoId: rascunho.utmCampanhaCampoId ? Number(rascunho.utmCampanhaCampoId) : null,
    utmConjuntoCampoId: rascunho.utmConjuntoCampoId ? Number(rascunho.utmConjuntoCampoId) : null,
    utmAnuncioCampoId: rascunho.utmAnuncioCampoId ? Number(rascunho.utmAnuncioCampoId) : null,
  };
}

const mesmo = (a: Rascunho, b: MapeamentoDoFunil | null) =>
  b !== null &&
  ITENS_DO_FUNIL_NO_CRM.every((item) => a[item]?.pipelineId === b[item].pipelineId && a[item]?.etapaId === b[item].etapaId) &&
  a.tratamento?.pipelineId === b.tratamento?.pipelineId &&
  a.tratamento?.etapaId === b.tratamento?.etapaId &&
  (a.valorConsultaCampoId || "") === (b.valorConsultaCampoId ? String(b.valorConsultaCampoId) : "") &&
  (a.valorPropostoCampoId || "") === (b.valorPropostoCampoId ? String(b.valorPropostoCampoId) : "") &&
  UTMS.every((utm) => (a[utm.chave] || "") === (b[utm.chave] ? String(b[utm.chave]) : ""));

/**
 * Qual etapa do Kommo representa cada item do funil. O lead conta no item
 * quando entra na etapa escolhida (ou numa etapa de item seguinte), pelo
 * historico de mudancas de etapa do Kommo.
 */
export function MapeamentoDoFunil({
  clienteId,
  conexao,
}: {
  readonly clienteId: string;
  readonly conexao: ConexaoDoKommo;
}) {
  const conectada = conexao.situacao !== "nao-conectada";
  // `key` no pai recria o rascunho quando a conexao (ou o mapeamento salvo) muda.
  const [rascunho, setRascunho] = useState<Rascunho>(() => rascunhoInicial(conexao));
  const mapear = useMapearKommo(clienteId);
  const pronto = completo(rascunho);
  const alterado = !mesmo(rascunho, conexao.mapeamento);

  function salvar() {
    if (pronto) mapear.mutate(pronto);
  }

  return (
    <div className="flex flex-col gap-secao">
      <section aria-labelledby="titulo-mapeamento" className="flex flex-col gap-bloco">
        <div className="flex flex-col gap-1">
          <h2 id="titulo-mapeamento" className="text-xl font-normal tracking-tight">
            Mapeamento do funil
          </h2>
          <p className="text-sm text-muted-foreground">
            Escolha a etapa do Kommo de cada item. O lead conta no item quando entra nessa etapa, ou numa etapa
            mais adiante do funil.
          </p>
        </div>

        <div className="overflow-hidden rounded-3xl bg-card shadow-sm ring-1 ring-border/70">
          <ul className="divide-y divide-border/70">
            <li className="grid gap-3 px-6 py-5 md:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] md:items-center">
              <ItemDoFunil rotulo={ROTULO_DA_ETAPA.gerenciador} descricao={DESCRICAO_DA_ETAPA.gerenciador} />
              <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                <Megaphone className="size-4" aria-hidden="true" />
                Leads reportados pelo Meta Ads ou Google Ads
              </span>
            </li>
            {ITENS_DO_FUNIL_NO_CRM.map((item) => (
              <li key={item} className="grid gap-3 px-6 py-5 md:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] md:items-center">
                <ItemDoFunil rotulo={ROTULO_DA_ETAPA[item]} descricao={DESCRICAO_DA_ETAPA[item]} />
                <SeletorDeEtapa
                  rotulo={ROTULO_DA_ETAPA[item]}
                  pipelines={conexao.pipelines}
                  valor={rascunho[item] ?? null}
                  desabilitado={!conectada}
                  aoEscolher={(etapa) => {
                    mapear.reset();
                    setRascunho((atual) => ({ ...atual, [item]: etapa }));
                  }}
                />
              </li>
            ))}
          </ul>

          <div className="flex flex-col gap-3 border-t bg-muted/30 px-6 py-4 md:flex-row md:items-center md:justify-between">
            <Rodape conectada={conectada} pronto={pronto !== null} alterado={alterado} mapear={mapear} />
            <Button disabled={!conectada || !pronto || !alterado || mapear.isPending} onClick={salvar}>
              {mapear.isPending ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              Salvar mapeamento
            </Button>
          </div>
        </div>
      </section>

      <section aria-labelledby="titulo-campos-de-valor" className="flex flex-col gap-bloco">
        <div className="flex flex-col gap-1">
          <h2 id="titulo-campos-de-valor" className="text-xl font-normal tracking-tight">
            Campos de valor
          </h2>
          <p className="text-sm text-muted-foreground">
            Campos personalizados do lead no Kommo. Sem eles, o faturamento de consulta fica zerado e o valor em
            aberto usa o preço do lead.
          </p>
        </div>

        <div className="overflow-hidden rounded-3xl bg-card shadow-sm ring-1 ring-border/70">
          <ul className="divide-y divide-border/70">
            <li className="grid gap-3 px-6 py-5 md:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] md:items-center">
              <ItemDoFunil
                rotulo="Tratamento"
                descricao="Etapa de quem entra no faturamento de tratamento (valor de venda do Kommo)."
              />
              <SeletorDeEtapa
                rotulo="Tratamento"
                pipelines={conexao.pipelines}
                valor={rascunho.tratamento ?? null}
                desabilitado={!conectada}
                aoEscolher={(etapa) => {
                  mapear.reset();
                  setRascunho((atual) => ({ ...atual, tratamento: etapa }));
                }}
              />
            </li>
            <li className="grid gap-3 px-6 py-5 md:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] md:items-center">
              <ItemDoFunil
                rotulo="Valor Consulta"
                descricao="Campo numérico. Soma no faturamento de consulta de quem passou na etapa de Consulta."
              />
              <CampoDeSelecao
                rotulo="Valor Consulta"
                textoVazio={camposMonetarios(conexao).length === 0 ? "Nenhum campo numérico" : "Campo do Kommo"}
                value={rascunho.valorConsultaCampoId ?? ""}
                disabled={!conectada}
                onChange={(evento) => {
                  mapear.reset();
                  setRascunho((atual) => ({ ...atual, valorConsultaCampoId: evento.target.value }));
                }}
                opcoes={camposMonetarios(conexao)}
              />
            </li>
            <li className="grid gap-3 px-6 py-5 md:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] md:items-center">
              <ItemDoFunil
                rotulo="Valor proposto"
                descricao="Campo numérico das propostas em aberto. Sem ele, o card usa o valor de venda do lead."
              />
              <CampoDeSelecao
                rotulo="Valor proposto"
                textoVazio={camposMonetarios(conexao).length === 0 ? "Nenhum campo numérico" : "Campo do Kommo"}
                value={rascunho.valorPropostoCampoId ?? ""}
                disabled={!conectada}
                onChange={(evento) => {
                  mapear.reset();
                  setRascunho((atual) => ({ ...atual, valorPropostoCampoId: evento.target.value }));
                }}
                opcoes={camposMonetarios(conexao)}
              />
            </li>
          </ul>

          <div className="flex flex-col gap-3 border-t bg-muted/30 px-6 py-4 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-muted-foreground">
              {camposMonetarios(conexao).length === 0
                ? "Sincronize o Kommo para listar os campos numéricos do lead."
                : "Salvar aplica as etapas e estes campos juntos."}
            </p>
            <Button disabled={!conectada || !pronto || !alterado || mapear.isPending} onClick={salvar}>
              {mapear.isPending ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              Salvar campos de valor
            </Button>
          </div>
        </div>
      </section>

      <section aria-labelledby="titulo-utms" className="flex flex-col gap-bloco">
        <div className="flex flex-col gap-1">
          <h2 id="titulo-utms" className="text-xl font-normal tracking-tight">
            UTMs
          </h2>
          <p className="text-sm text-muted-foreground">
            Qual campo de rastreio do Kommo é Campanha, Conjunto e Anúncio. Sem escolha, usa utm_campaign,
            utm_content e utm_term.
          </p>
        </div>

        <div className="overflow-hidden rounded-3xl bg-card shadow-sm ring-1 ring-border/70">
          <ul className="divide-y divide-border/70">
            {UTMS.map((utm) => (
              <li key={utm.chave} className="grid gap-3 px-6 py-5 md:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] md:items-center">
                <ItemDoFunil rotulo={utm.rotulo} descricao={utm.descricao} />
                <CampoDeSelecao
                  rotulo={utm.rotulo}
                  textoVazio={camposDeUtm(conexao).length === 0 ? "Nenhum campo de rastreio" : "Campo do Kommo"}
                  value={rascunho[utm.chave] ?? ""}
                  disabled={!conectada}
                  onChange={(evento) => {
                    mapear.reset();
                    setRascunho((atual) => ({ ...atual, [utm.chave]: evento.target.value }));
                  }}
                  opcoes={camposDeUtm(conexao)}
                />
              </li>
            ))}
          </ul>

          <div className="flex flex-col gap-3 border-t bg-muted/30 px-6 py-4 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-muted-foreground">
              {camposDeUtm(conexao).length === 0
                ? "Sincronize o Kommo para listar os campos de rastreio do lead."
                : "Salvar aplica as etapas e estas UTMs juntas."}
            </p>
            <Button disabled={!conectada || !pronto || !alterado || mapear.isPending} onClick={salvar}>
              {mapear.isPending ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              Salvar UTMs
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

function camposMonetarios(conexao: ConexaoDoKommo) {
  const tipos = new Set<string>(TIPOS_DE_CAMPO_MONETARIOS);
  return conexao.camposPersonalizados
    .filter((campo) => tipos.has(campo.tipo))
    .map((campo) => ({ valor: String(campo.id), rotulo: campo.nome }));
}

function camposDeUtm(conexao: ConexaoDoKommo) {
  return conexao.camposPersonalizados
    .filter((campo) => campo.tipo === "tracking_data" || /^utm[_ ]/i.test(campo.nome))
    .map((campo) => ({ valor: String(campo.id), rotulo: campo.nome }));
}

function idsDasUtms(conexao: ConexaoDoKommo, mapeamento: MapeamentoDoFunil | null) {
  const porNome = (nome: string) =>
    conexao.camposPersonalizados.find(
      (campo) => campo.tipo === "tracking_data" && campo.nome.toLowerCase() === nome,
    )?.id;
  return {
    utmCampanhaCampoId: String(mapeamento?.utmCampanhaCampoId ?? porNome("utm_campaign") ?? ""),
    utmConjuntoCampoId: String(mapeamento?.utmConjuntoCampoId ?? porNome("utm_content") ?? ""),
    utmAnuncioCampoId: String(mapeamento?.utmAnuncioCampoId ?? porNome("utm_term") ?? ""),
  };
}

function ItemDoFunil({ rotulo, descricao }: { readonly rotulo: string; readonly descricao: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-sm font-semibold">{rotulo}</span>
      <span className="text-xs leading-snug text-muted-foreground">{descricao}</span>
    </div>
  );
}

function Rodape({
  conectada,
  pronto,
  alterado,
  mapear,
}: {
  readonly conectada: boolean;
  readonly pronto: boolean;
  readonly alterado: boolean;
  readonly mapear: ReturnType<typeof useMapearKommo>;
}) {
  const [texto, tom] = !conectada
    ? ["Conecte o Kommo para escolher as etapas.", "neutro"]
    : mapear.isError
      ? [mensagemDoErro(mapear.error), "erro"]
      : mapear.isSuccess
        ? ["Mapeamento salvo. Os dados do Kommo estão sendo sincronizados.", "sucesso"]
        : !pronto
          ? ["Escolha uma etapa para cada item.", "neutro"]
          : alterado
            ? ["Há alterações não salvas.", "neutro"]
            : ["Mapeamento em uso pelo Dashboard.", "sucesso"];

  return (
    <p
      role={tom === "erro" ? "alert" : "status"}
      className={cn(
        "flex items-center gap-2 text-sm",
        tom === "erro" ? "text-destructive" : tom === "sucesso" ? "text-success" : "text-muted-foreground",
      )}
    >
      {tom === "sucesso" ? <CheckCircle2 className="size-4" aria-hidden="true" /> : null}
      {texto}
    </p>
  );
}
